// components/SectionImageUpload.js
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Alert,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { 
  CloudUpload, 
  Delete, 
  InsertDriveFile,
  Warning,
  CheckCircle
} from '@mui/icons-material';
import { API_URL } from '../services/appData';

const SectionImageUpload = ({ 
  value, 
  onChange, 
  error, 
  helperText, 
  language, 
  setIsUploading,
  maxSize = 512, // KB for ICO files (usually small)
  ...props 
}) => {
  const [preview, setPreview] = useState(value);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [fileInfo, setFileInfo] = useState(null);
  const fileInputRef = useRef(null);

  // Validate ICO file
  const validateICOFile = (file) => {
    // Check file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'ico') {
      setFileError(language === 'ar' 
        ? 'يجب أن يكون الملف بصيغة ICO فقط' 
        : 'File must be ICO format only');
      return false;
    }

    // Check MIME type (some browsers may report differently)
    const validMimeTypes = ['image/x-icon', 'image/vnd.microsoft.icon'];
    if (!validMimeTypes.includes(file.type) && file.type !== '') {
      // Some browsers don't properly detect ICO mime type, so we rely more on extension
      console.warn('Unusual MIME type for ICO:', file.type);
    }

    // Check file size (maxSize in KB)
    const maxSizeBytes = maxSize * 1024;
    if (file.size > maxSizeBytes) {
      setFileError(language === 'ar'
        ? `حجم الملف يجب أن لا يتجاوز ${maxSize} كيلوبايت`
        : `File size must not exceed ${maxSize} KB`);
      return false;
    }

    // Check minimum size (at least 1KB)
    if (file.size < 1024) {
      setFileError(language === 'ar'
        ? 'حجم الملف صغير جداً (الحد الأدنى 1 كيلوبايت)'
        : 'File size is too small (minimum 1 KB)');
      return false;
    }

    return true;
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset errors
    setFileError('');
    setFileInfo(null);

    // Validate ICO file
    if (!validateICOFile(file)) {
      return;
    }

    setUploading(true);
    if (setIsUploading) setIsUploading(true);
    
    try {
      // Create preview for ICO (by converting to PNG for display)
      const previewUrl = await convertICOToPreview(file);
      setPreview(previewUrl);
      
      // Get file info
      setFileInfo({
        name: file.name,
        size: `${(file.size / 1024).toFixed(2)} KB`,
        type: 'ICO'
      });

      // Upload to server
      const imageUrl = await uploadImage(file);
      onChange(imageUrl);

    } catch (error) {
      console.error('Upload failed:', error);
      setFileError(error.message || (language === 'ar' 
        ? 'فشل رفع الملف' 
        : 'File upload failed'));
    } finally {
      setUploading(false);
      if (setIsUploading) setIsUploading(false);
    }
  };

  // Convert ICO file to preview image
  const convertICOToPreview = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target.result);
      };
      reader.onerror = () => {
        reject(new Error(language === 'ar' 
          ? 'فشل في قراءة الملف' 
          : 'Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('is_sec', 'true'); // Important: Tell backend this is a section icon

    const response = await fetch(`${API_URL}/upload`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Upload failed');
    }

    const data = await response.json();
    return data.imageUrl;
  };

  const handleRemoveImage = () => {
    setPreview(null);
    setFileInfo(null);
    setFileError('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (file) {
      // Create a synthetic event to reuse handleFileSelect logic
      const syntheticEvent = { target: { files: [file] } };
      handleFileSelect(syntheticEvent);
    }
  };

  return (
    <Box sx={{ mt: 2, ...props.sx }}>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InsertDriveFile fontSize="small" />
        {language === 'ar' ? 'أيقونة القسم (ICO)' : 'Section Icon (ICO)'}
        <Chip 
          label="ICO only" 
          size="small" 
          color="primary" 
          variant="outlined"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      </Typography>
      
      <input
        type="file"
        accept=".ico,image/x-icon,image/vnd.microsoft.icon"
        onChange={handleFileSelect}
        ref={fileInputRef}
        style={{ display: 'none' }}
        id="section-icon-upload"
      />
      
      {preview ? (
        <Card variant="outlined" sx={{ mb: 2, position: 'relative' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            {/* Preview of ICO */}
            <Box
              sx={{
                width: 64,
                height: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'action.hover',
                borderRadius: 2,
                position: 'relative'
              }}
            >
              <img
                src={preview}
                alt="Section icon preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              />
              <IconButton
                onClick={handleRemoveImage}
                size="small"
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { backgroundColor: 'error.light', color: 'white' }
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
            
            {/* File Info */}
            <Box sx={{ flex: 1 }}>
              {fileInfo && (
                <>
                  <Typography variant="body2" fontWeight="medium">
                    {fileInfo.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fileInfo.size} • {fileInfo.type}
                  </Typography>
                </>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                <Typography variant="caption" color="success.main">
                  {language === 'ar' ? 'تم رفع الأيقونة بنجاح' : 'Icon uploaded successfully'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={{
            border: error || fileError ? '2px dashed #f44336' : '2px dashed #e0e0e0',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backgroundColor: '#201f1b',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.selected'
            }
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            accept=".ico,image/x-icon,image/vnd.microsoft.icon"
            onChange={handleFileSelect}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          
          {uploading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                {language === 'ar' ? 'جاري رفع الأيقونة...' : 'Uploading icon...'}
              </Typography>
            </Box>
          ) : (
            <>
              <CloudUpload sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                {language === 'ar' ? 'إضغط أو اسحب ملف ICO هنا' : 'Click or drag ICO file here'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {language === 'ar' 
                  ? `صيغة ICO فقط، الحد الأقصى ${maxSize} كيلوبايت` 
                  : `ICO format only, max ${maxSize} KB`}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Chip 
                  icon={<Warning sx={{ fontSize: 14 }} />}
                  label="16x16"
                  size="small"
                  variant="outlined"
                />
                <Chip 
                  icon={<Warning sx={{ fontSize: 14 }} />}
                  label="32x32"
                  size="small"
                  variant="outlined"
                />
                <Chip 
                  icon={<Warning sx={{ fontSize: 14 }} />}
                  label="64x64"
                  size="small"
                  variant="outlined"
                />
              </Box>
            </>
          )}
        </Box>
      )}
      
      {/* Error Messages */}
      {(error || fileError) && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {helperText || fileError || error}
        </Alert>
      )}
      
      {/* Info Alert */}
      {!preview && !fileError && !error && (
        <Alert severity="info" sx={{ mt: 1 }} icon={<InsertDriveFile />}>
          <Typography variant="caption">
            {language === 'ar'
              ? 'يوصى باستخدام أيقونات بحجم 32x32 أو 64x64 بكسل للحصول على أفضل جودة عرض'
              : 'Recommended icon sizes: 32x32 or 64x64 pixels for best display quality'}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default SectionImageUpload;